import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiBellRingOutline, mdiCancel, mdiDownload } from '@mdi/js'
import type { SignatureRequest } from '../../api/schema'
import { useCan } from '../../contexts/PermissionsContext'
import { useSignatureRequestAction, useSignatureRequests, type SignatureRequestFilter } from '../../hooks/useSignatures'
import { useFormatters } from '../../lib/format'
import { apiBaseUrl } from '../../api/client'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { requestStatusVariant, signerStatusVariant } from './signature-utils'

/**
 * The follow-up panel of the signature requests on a document or on the act
 * they carry (spec 2026-10-05-05 §5): status per signer, remind, cancel,
 * download the sealed PDF. Hidden without `signatures.read`, and when there is
 * nothing to follow.
 */
export function SignatureRequestsPanel({ org, filter, testId = 'signature-requests-panel' }: { org: string; filter: SignatureRequestFilter; testId?: string }) {
    const { t } = useTranslation('signatures')
    const canRead = useCan('signatures.read')
    const { data } = useSignatureRequests(org, filter, canRead)
    const requests = data?.data ?? []
    if (!canRead || requests.length === 0) return null
    return (
        <section className="mt-6 rounded-lg border p-4" data-testid={testId}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('panel.title')}</h2>
            <ul className="space-y-4">
                {requests.map((r) => (
                    <SignatureRequestCard key={r.uid} org={org} request={r} />
                ))}
            </ul>
        </section>
    )
}

export function SignatureRequestCard({ org, request }: { org: string; request: SignatureRequest }) {
    const { t } = useTranslation('signatures')
    const { formatDate } = useFormatters()
    const canWrite = useCan('signatures.write')
    const action = useSignatureRequestAction(org)
    const fmt = (d?: string) => (d ? formatDate(d, { dateStyle: 'short', timeStyle: 'short' }) : '')
    const open = request.status === 'pending' || request.status === 'draft'
    return (
        <li className="space-y-2" data-testid={`signature-request-${request.uid}`}>
            <div className="flex flex-wrap items-center gap-2">
                <Link
                    to="/$org/signatures/$uid"
                    params={{ org, uid: request.uid }}
                    className="font-medium text-foreground hover:underline"
                    data-testid={`signature-request-link-${request.uid}`}
                >
                    {request.title}
                </Link>
                <Badge variant={requestStatusVariant(request.status)} data-testid={`signature-request-status-${request.uid}`}>
                    {t(`request_status.${request.status}`)}
                </Badge>
                {request.ordered && <Badge variant="outline">{t('panel.ordered')}</Badge>}
                <span className="text-xs text-muted-foreground">{t('panel.expires', { at: fmt(request.expires_at) })}</span>
                <div className="ml-auto flex gap-1">
                    {request.signed_document_uid && (
                        <Button asChild variant="outline" size="sm">
                            <a
                                href={`${apiBaseUrl}/api/v1/organizations/${org}/documents/${request.signed_document_uid}/content?download=true`}
                                aria-label={t('panel.download')}
                                title={t('panel.download')}
                                data-testid={`signature-request-download-${request.uid}`}
                            >
                                <AppIcon path={mdiDownload} size="sm" className="md:mr-1" />
                                <span className="hidden md:inline">{t('panel.download')}</span>
                            </a>
                        </Button>
                    )}
                    {canWrite && request.status === 'pending' && (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={action.isPending}
                            onClick={() => action.mutate({ uid: request.uid, action: 'remind' })}
                            aria-label={t('panel.remind')}
                            title={t('panel.remind')}
                            data-testid={`signature-request-remind-${request.uid}`}
                        >
                            <AppIcon path={mdiBellRingOutline} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('panel.remind')}</span>
                        </Button>
                    )}
                    {canWrite && open && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            disabled={action.isPending}
                            onClick={() => action.mutate({ uid: request.uid, action: 'cancel' })}
                            aria-label={t('panel.cancel')}
                            title={t('panel.cancel')}
                            data-testid={`signature-request-cancel-${request.uid}`}
                        >
                            <AppIcon path={mdiCancel} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{t('panel.cancel')}</span>
                        </Button>
                    )}
                </div>
            </div>
            <ol className="space-y-1 pl-1">
                {request.signers.map((s) => (
                    <li key={s.uid} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`signature-signer-${s.uid}`}>
                        <span className="w-5 text-muted-foreground">{s.position}.</span>
                        <span className="text-foreground">{s.name}</span>
                        {s.role && <span className="text-muted-foreground">· {s.role}</span>}
                        <Badge variant={signerStatusVariant(s.status)} data-testid={`signature-signer-status-${s.uid}`}>
                            {t(`signer_status.${s.status}`)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                            {s.signed_at
                                ? t('panel.signed_at', { at: fmt(s.signed_at) })
                                : s.declined_at
                                    ? t('panel.declined_at', { at: fmt(s.declined_at), reason: s.decline_reason ?? '' })
                                    : s.opened_at
                                        ? t('panel.opened_at', { at: fmt(s.opened_at) })
                                        : s.notified_at
                                            ? t('panel.notified_at', { at: fmt(s.notified_at) })
                                            : ''}
                        </span>
                        {s.locked_until && new Date(s.locked_until) > new Date() && (
                            <Badge variant="warning">{t('panel.locked')}</Badge>
                        )}
                    </li>
                ))}
            </ol>
            {request.delivery_failures && request.delivery_failures.length > 0 && (
                <p className="text-sm text-destructive">{t('panel.delivery_failed')}</p>
            )}
        </li>
    )
}
